import type {
  LightingAdvisorQuestion,
  LightingAdvisorInput
} from '~/types/lightingAdvisor';

type QuestionDefinition = {
  id: string;
  options: readonly string[];
};

const LIGHT_TYPE_TOOLTIP_VALUES = [
  'back_light',
  'bar_light',
  'coaxial_light',
  'dark_field_light',
  'dome_light',
  'flat_dome_light',
  'ring_light',
  'spot_light'
] as const;

const QUESTION_DEFINITIONS: QuestionDefinition[] = [
  {
    id: 'a2s1q1',
    options: ['transmitted_light', 'dark_field', 'incident_light']
  },
  {
    id: 'a2s1q2',
    options: ['low_reflection', 'medium_reflection', 'strong_reflection']
  },
  { id: 'a2s1q3', options: [...LIGHT_TYPE_TOOLTIP_VALUES] },
  { id: 'a2s2q1', options: [] },
  { id: 'a2s2q2', options: [] },
  { id: 'a2s2q3', options: ['yes', 'no'] },
  { id: 'a2s2q4', options: ['yes', 'no'] },
  { id: 'a2s3q1', options: ['white', 'colored', 'infrared'] },
  {
    id: 'a2s3q2',
    options: [
      'blue_green_features',
      'red_yellow_features',
      'infrared',
      'swir',
      'uv'
    ]
  },
  {
    id: 'a2s3q3',
    options: [
      'uv_365nm',
      'uv_395nm',
      'uv_396nm',
      'ir_850nm',
      'swir_1050nm',
      'swir_1200nm',
      'swir_1300nm',
      'swir_1450nm',
      'swir_1550nm',
      'swir_1650nm'
    ]
  },
  { id: 'a2s4q0', options: ['1', '2', '3', '4'] },
  {
    id: 'a2s4q1',
    options: ['flashing', 'steady_light', 'dim', 'brightness_control']
  },
  {
    id: 'a2s5q1',
    options: [
      'factory_automation',
      'medical',
      'retail',
      'traffic',
      'logistics',
      'agriculture',
      'sports',
      'further_application'
    ]
  },
  { id: 'a2s5q2', options: ['basler', 'thirdparty', 'none'] }
];

const ENGLISH_FALLBACKS: Record<string, string> = {
  'lightingAdvisor.question.a2s1q1.content':
    'How should the object be illuminated?',
  'lightingAdvisor.question.a2s1q1.transmitted_light': 'Transmitted light',
  'lightingAdvisor.question.a2s1q1.dark_field': 'Dark field',
  'lightingAdvisor.question.a2s1q1.incident_light': 'Incident light',
  'lightingAdvisor.question.a2s1q2.content': 'How reflective is the object?',
  'lightingAdvisor.question.a2s1q2.low_reflection': 'Low reflection',
  'lightingAdvisor.question.a2s1q2.medium_reflection': 'Medium reflection',
  'lightingAdvisor.question.a2s1q2.strong_reflection': 'Strong reflection',
  'lightingAdvisor.question.a2s1q3.content': 'Which light type do you prefer?',
  'lightingAdvisor.question.a2s1q3.back_light': 'Back light',
  'lightingAdvisor.question.a2s1q3.bar_light': 'Bar light',
  'lightingAdvisor.question.a2s1q3.coaxial_light': 'Coaxial light',
  'lightingAdvisor.question.a2s1q3.dark_field_light': 'Dark field light',
  'lightingAdvisor.question.a2s1q3.dome_light': 'Dome light',
  'lightingAdvisor.question.a2s1q3.flat_dome_light': 'Flat dome light',
  'lightingAdvisor.question.a2s1q3.ring_light': 'Ring light',
  'lightingAdvisor.question.a2s1q3.spot_light': 'Spot light',
  'lightingAdvisor.question.a2s2q1.content': 'Working distance (mm)',
  'lightingAdvisor.question.a2s2q2.content': 'Object size (mm)',
  'lightingAdvisor.question.a2s2q3.content': 'Is the object moving?',
  'lightingAdvisor.question.a2s2q4.content':
    'Are there ambient light interferences?',
  'lightingAdvisor.common.yes': 'Yes',
  'lightingAdvisor.common.no': 'No',
  'lightingAdvisor.question.a2s3q1.content': 'Which light color do you need?',
  'lightingAdvisor.question.a2s3q1.white': 'White',
  'lightingAdvisor.question.a2s3q1.colored': 'Colored',
  'lightingAdvisor.question.a2s3q1.infrared': 'Infrared / UV',
  'lightingAdvisor.question.a2s3q2.content':
    'Which object features should be highlighted?',
  'lightingAdvisor.question.a2s3q2.blue_green_features':
    'Blue / green features',
  'lightingAdvisor.question.a2s3q2.red_yellow_features':
    'Red / yellow features',
  'lightingAdvisor.question.a2s3q2.infrared': 'Infrared',
  'lightingAdvisor.question.a2s3q2.swir': 'SWIR',
  'lightingAdvisor.question.a2s3q2.uv': 'UV',
  'lightingAdvisor.question.a2s3q3.content': 'Peak wavelength',
  'lightingAdvisor.question.a2s3q3.uv_365nm': '365 nm',
  'lightingAdvisor.question.a2s3q3.uv_395nm': '395 nm',
  'lightingAdvisor.question.a2s3q3.uv_396nm': '396 nm',
  'lightingAdvisor.question.a2s3q3.ir_850nm': '850 nm',
  'lightingAdvisor.question.a2s3q3.swir_1050nm': '1050 nm',
  'lightingAdvisor.question.a2s3q3.swir_1200nm': '1200 nm',
  'lightingAdvisor.question.a2s3q3.swir_1300nm': '1300 nm',
  'lightingAdvisor.question.a2s3q3.swir_1450nm': '1450 nm',
  'lightingAdvisor.question.a2s3q3.swir_1550nm': '1550 nm',
  'lightingAdvisor.question.a2s3q3.swir_1650nm': '1650 nm',
  'lightingAdvisor.question.a2s4q0.content':
    'How many lights should be installed in your vision system?',
  'lightingAdvisor.question.a2s4q1.content':
    'Which light functions do you need?',
  'lightingAdvisor.question.a2s4q1.flashing': 'Flashing - Pulsing',
  'lightingAdvisor.question.a2s4q1.steady_light': 'Permanent Light',
  'lightingAdvisor.question.a2s4q1.dim': 'Dimming',
  'lightingAdvisor.question.a2s4q1.brightness_control': 'Flashing - Overdrive',
  'lightingAdvisor.question.a2s5q1.content': 'Application area',
  'lightingAdvisor.question.a2s5q1.factory_automation': 'Factory Automation',
  'lightingAdvisor.question.a2s5q1.medical': 'Medical & Life Science',
  'lightingAdvisor.question.a2s5q1.retail': 'Retail',
  'lightingAdvisor.question.a2s5q1.traffic': 'Traffic & Transportation',
  'lightingAdvisor.question.a2s5q1.logistics': 'Logistics',
  'lightingAdvisor.question.a2s5q1.agriculture': 'Agriculture',
  'lightingAdvisor.question.a2s5q1.sports': 'Sports',
  'lightingAdvisor.question.a2s5q1.further_application': 'Further Application',
  'lightingAdvisor.question.a2s5q2.content': 'Do you already own a camera?',
  'lightingAdvisor.question.a2s5q2.basler': 'Yes, I own a Basler camera',
  'lightingAdvisor.question.a2s5q2.thirdparty': 'Yes, a third-party camera',
  'lightingAdvisor.question.a2s5q2.none': 'No',
  'lightingAdvisor.step.1.label': 'Lighting Type',
  'lightingAdvisor.step.1.title': 'Which lighting type suits your application?',
  'lightingAdvisor.step.2.label': 'General Conditions',
  'lightingAdvisor.step.2.title':
    'Which framework conditions influence your application?',
  'lightingAdvisor.step.3.label': 'Light Spectrum',
  'lightingAdvisor.step.3.title':
    'What light do you need for your application?',
  'lightingAdvisor.step.4.label': 'Light control',
  'lightingAdvisor.step.4.title':
    'How should the light be controlled in operation?',
  'lightingAdvisor.step.5.label': 'System Tuning',
  'lightingAdvisor.step.5.title':
    'What kind of camera do you use for your vision system?',
  'lightingAdvisor.common.pleaseSelect': 'Please select'
};

export function dictText(
  dictionary: Record<string, string | undefined> | undefined,
  key: string
): string {
  return dictionary?.[key] ?? ENGLISH_FALLBACKS[key] ?? key;
}

function optionalDictText(
  dictionary: Record<string, string | undefined> | undefined,
  key: string
): string | null {
  return dictionary?.[key] ?? ENGLISH_FALLBACKS[key] ?? null;
}

function questionKey(id: string): string {
  return `lightingAdvisor.question.${id}`;
}

function questionStep(id: string): number {
  return Number(id.match(/a2s(\d+)/)?.[1] ?? 0);
}

function questionOrder(id: string): number {
  const question = Number(id.match(/q(\d+)/)?.[1] ?? 0);
  return questionStep(id) === 4 ? question + 1 : question;
}

function optionKey(questionId: string, value: string): string {
  if (questionId === 'a2s2q3' || questionId === 'a2s2q4') {
    return `lightingAdvisor.common.${value}`;
  }
  if (questionId === 'a2s4q0') {
    return value;
  }
  return `${questionKey(questionId)}.${value}`;
}

function buildQuestionNotes(
  definition: QuestionDefinition,
  dictionary: Record<string, string | undefined> | undefined
): LightingAdvisorQuestion['notes'] {
  const notes: Array<{
    type: string;
    textKey: string;
    requirement: string | null;
  }> = [];
  const key = questionKey(definition.id);

  if (definition.id === 'a2s1q3') {
    for (const lightType of LIGHT_TYPE_TOOLTIP_VALUES) {
      notes.push({
        type: 'tooltip',
        textKey: `${key}.tooltip_${lightType}`,
        requirement: lightType
      });
    }
  } else if (definition.id === 'a2s3q2') {
    notes.push(
      {
        type: 'tooltip_colored',
        textKey: `${key}.tooltip_colored_light`,
        requirement: null
      },
      {
        type: 'tooltip_infrared',
        textKey: `${key}.tooltip_infrared_light`,
        requirement: null
      }
    );
  } else {
    notes.push({
      type: 'tooltip',
      textKey: `${key}.tooltip`,
      requirement: null
    });
  }

  if (definition.id === 'a2s3q1') {
    notes.push({
      type: 'warning_content',
      textKey: `${key}.warning_content`,
      requirement: null
    });
  }

  return notes.flatMap((note) => {
    const text = optionalDictText(dictionary, note.textKey);
    // An empty string means the key exists but its Rich Text carries no plain
    // text (e.g. an illustration-only tooltip) — keep it so the info modal can
    // still render the document.
    return text != null
      ? [
          {
            type: note.type,
            text,
            requirement: note.requirement,
            textKey: note.textKey
          }
        ]
      : [];
  });
}

export function buildLightingAdvisorQuestions(
  dictionary?: Record<string, string | undefined>,
  steps?: number[]
): LightingAdvisorQuestion[] {
  const allowedSteps = steps?.length ? new Set(steps) : null;
  return QUESTION_DEFINITIONS.filter((definition) =>
    allowedSteps ? allowedSteps.has(questionStep(definition.id)) : true
  ).map((definition) => {
    const key = questionKey(definition.id);
    const inputs: LightingAdvisorInput[] = definition.options.map((value) => ({
      value,
      text: dictText(dictionary, optionKey(definition.id, value))
    }));
    return {
      id: definition.id,
      order: questionOrder(definition.id),
      label:
        optionalDictText(dictionary, `${key}.label`) ??
        dictText(dictionary, `${key}.content`),
      content: dictText(dictionary, `${key}.content`),
      inputs,
      notes: buildQuestionNotes(definition, dictionary)
    };
  });
}

export function lightingAdvisorQuestionsForPlp(
  dictionary?: Record<string, string | undefined>
): LightingAdvisorQuestion[] {
  return buildLightingAdvisorQuestions(dictionary, [1, 2, 3]).filter(
    (question) => question.id !== 'a2s2q3' && question.id !== 'a2s2q4'
  );
}

export function applyLightingAdvisorDictionary(
  questions: LightingAdvisorQuestion[],
  dictionary?: Record<string, string | undefined>
): LightingAdvisorQuestion[] {
  const localized = buildLightingAdvisorQuestions(dictionary);
  const byId = new Map(localized.map((question) => [question.id, question]));
  return questions.map((question) => {
    const match = byId.get(question.id);
    if (!match) {
      return question;
    }
    return {
      ...question,
      label: match.label,
      content: match.content,
      inputs: match.inputs,
      notes: match.notes.length ? match.notes : question.notes
    };
  });
}
